import { z } from 'zod';

import { foldDigitsToAscii } from '@shared/format';

import { feePercentToBasisPoints, splitCallbackEndpoint } from '../_shared/constants';
import { copy } from './copy';

/**
 * The gateway record form — three fields, and **the credential is not one of
 * them, on purpose**. A secret is not edited beside a display name; it has its
 * own action, its own dialog, and a value nothing on this surface reads back.
 *
 * The callback endpoint is captured as **one address**, because one address is
 * what the operator also holds in the provider's own console and the two have to
 * match character for character. The wire stores it as an origin and a path, and
 * the split at the request boundary is mechanical — `URL`'s own parse, not a
 * rule this form invented. A string the parser refuses is a field error here
 * rather than a request built from a guess.
 *
 * The fee is contracted as a percentage and stored in basis points; the
 * conversion lives in `_shared/constants.ts` so the two directions cannot drift.
 *
 * **The schema validates; it does not convert.** `toGatewayWriteBody` performs
 * both mappings once the values are known good.
 */

export const gatewayFormSchema = z.object({
  name: z.string().trim().min(1, copy.validation.nameRequired).max(255, copy.validation.nameTooLong),

  callbackEndpoint: z
    .string()
    .trim()
    .min(1, copy.validation.callbackRequired)
    .refine((value) => splitCallbackEndpoint(value) !== null, copy.validation.callbackInvalid),

  feePercent: z
    .string()
    .trim()
    // An operator on an Arabic keyboard types `٢٫٥`; rejecting that rejects the
    // glyph their keyboard produced rather than the number they meant. The
    // Arabic decimal separator folds to the ASCII point in the same pass.
    .transform((value) => foldDigitsToAscii(value).replace('٫', '.'))
    .pipe(
      z
        .string()
        .min(1, copy.validation.feeRequired)
        .regex(/^\d+(\.\d{1,2})?$/, copy.validation.feeRange),
    )
    .refine((value) => Number(value) >= 0 && Number(value) <= 100, copy.validation.feeRange),
});

export type GatewayFormValues = z.infer<typeof gatewayFormSchema>;

/**
 * The request body. Returns `null` only if the endpoint stopped parsing between
 * validation and submit, which the caller reports as a field error rather than
 * sending a request built from a guess.
 */
export function toGatewayWriteBody(values: GatewayFormValues) {
  const split = splitCallbackEndpoint(values.callbackEndpoint);
  if (!split) return null;

  return {
    name: values.name,
    base_url: split.baseUrl,
    callback_path: split.callbackPath,
    fee_bps: feePercentToBasisPoints(Number(values.feePercent)),
  };
}

/**
 * The rotation field, validated on its own.
 *
 * It is **not** part of the record form and never shares a submit with it: the
 * value is read from an uncontrolled input at the moment of the write and the
 * element is cleared on close whether the rotation committed or was cancelled. A
 * secret left in an element's `value` is readable from the browser's own tools
 * after the dialog closed over it.
 */
export const rotationSchema = z.string().trim().min(1, copy.rotate.newRequired);
